/**
 * TimeSlotGrid Component
 * Displays a grid of time slots for selection
 *
 * @version 2025.1.0
 */
import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Divider,
  useTheme,
} from '@mui/material';
import { TimeSlot, generateTimeSlots } from '../../../types/professionalSchedule';

interface TimeSlotGridProps {
  slotDuration: number;
  selectedSlots: TimeSlot[];
  onChange: (slots: TimeSlot[]) => void;
  disabled?: boolean;
  startHour?: number;
  endHour?: number;
}

const TimeSlotGrid: React.FC<TimeSlotGridProps> = ({
  slotDuration,
  selectedSlots,
  onChange,
  disabled = false,
  startHour = 7,
  endHour = 20,
}) => {
  const theme = useTheme();

  // Generate all possible slots for the day
  const allSlots = useMemo(() => {
    const startTime = `${String(startHour).padStart(2, '0')}:00`;
    const endTime = `${String(endHour).padStart(2, '0')}:00`;
    return generateTimeSlots(startTime, endTime, slotDuration);
  }, [slotDuration, startHour, endHour]);

  // Split slots into morning and afternoon
  const morningSlots = useMemo(() => {
    return allSlots.filter(slot => {
      const hour = parseInt(slot.startTime.split(':')[0] ?? '0');
      return hour < 12;
    });
  }, [allSlots]);

  const afternoonSlots = useMemo(() => {
    return allSlots.filter(slot => {
      const hour = parseInt(slot.startTime.split(':')[0] ?? '0');
      return hour >= 12;
    });
  }, [allSlots]);

  // Check if a slot is selected
  const isSelected = (slot: TimeSlot): boolean => {
    return selectedSlots.some(s => s.startTime === slot.startTime && s.endTime === slot.endTime);
  };

  // Toggle a single slot
  const toggleSlot = (slot: TimeSlot) => {
    if (disabled) return;

    const isCurrentlySelected = isSelected(slot);
    if (isCurrentlySelected) {
      onChange(selectedSlots.filter(s => !(s.startTime === slot.startTime && s.endTime === slot.endTime)));
    } else {
      onChange([...selectedSlots, { ...slot, selected: true }]);
    }
  };

  // Select all morning slots
  const selectAllMorning = () => {
    if (disabled) return;
    const morningSelected = morningSlots.map(s => ({ ...s, selected: true }));
    const afternoonSelected = selectedSlots.filter(s => {
      const hour = parseInt(s.startTime.split(':')[0] ?? '0');
      return hour >= 12;
    });
    onChange([...morningSelected, ...afternoonSelected]);
  };

  // Clear morning slots
  const clearMorning = () => {
    if (disabled) return;
    onChange(selectedSlots.filter(s => {
      const hour = parseInt(s.startTime.split(':')[0] ?? '0');
      return hour >= 12;
    }));
  };

  // Select all afternoon slots
  const selectAllAfternoon = () => {
    if (disabled) return;
    const afternoonSelected = afternoonSlots.map(s => ({ ...s, selected: true }));
    const morningSelected = selectedSlots.filter(s => {
      const hour = parseInt(s.startTime.split(':')[0] ?? '0');
      return hour < 12;
    });
    onChange([...morningSelected, ...afternoonSelected]);
  };

  // Clear afternoon slots
  const clearAfternoon = () => {
    if (disabled) return;
    onChange(selectedSlots.filter(s => {
      const hour = parseInt(s.startTime.split(':')[0] ?? '0');
      return hour < 12;
    }));
  };

  // Select all slots
  const selectAll = () => {
    if (disabled) return;
    onChange(allSlots.map(s => ({ ...s, selected: true })));
  };

  // Clear all slots
  const clearAll = () => {
    if (disabled) return;
    onChange([]);
  };

  const renderSlotGroup = (slots: TimeSlot[], title: string, onSelectAll: () => void, onClear: () => void) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
        <Box>
          <Button size="small" onClick={onSelectAll} disabled={disabled}>
            Selecionar todos
          </Button>
          <Button size="small" onClick={onClear} disabled={disabled} color="inherit">
            Limpar
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {slots.map((slot) => (
          <Chip
            key={slot.startTime}
            label={slot.startTime}
            onClick={() => toggleSlot(slot)}
            color={isSelected(slot) ? 'primary' : 'default'}
            variant={isSelected(slot) ? 'filled' : 'outlined'}
            disabled={disabled}
            sx={{
              minWidth: 70,
              cursor: disabled ? 'not-allowed' : 'pointer',
              '&:hover': {
                backgroundColor: isSelected(slot)
                  ? theme.palette.primary.dark
                  : theme.palette.action.hover,
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Duração do slot: <strong>{slotDuration} minutos</strong>
        </Typography>
        <Box>
          <Button size="small" onClick={selectAll} disabled={disabled}>
            Selecionar todos
          </Button>
          <Button size="small" onClick={clearAll} disabled={disabled} color="inherit">
            Limpar tudo
          </Button>
        </Box>
      </Box>

      {morningSlots.length > 0 && renderSlotGroup(morningSlots, 'Manhã (até 12h)', selectAllMorning, clearMorning)}

      {morningSlots.length > 0 && afternoonSlots.length > 0 && <Divider sx={{ my: 2 }} />}

      {afternoonSlots.length > 0 && renderSlotGroup(afternoonSlots, 'Tarde (12h - 18h) / Noite', selectAllAfternoon, clearAfternoon)}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        {selectedSlots.length} horário(s) selecionado(s)
      </Typography>
    </Box>
  );
};

export default TimeSlotGrid;
